import { useEffect, useRef, useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";

import { PreferenceControls } from "@/components/app-settings/preference-controls";
import { useAppSettings } from "@/components/app-settings/app-settings-provider";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { apiGet, apiPost } from "@/lib/client/api";

type PublicQuoteMeta = {
  projectTitle: string;
  quotationNo: string;
  validUntil: number | null;
  requiresPassword: boolean;
  publicDownloadPdf: boolean;
  publicDownloadExcel: boolean;
};

type PublicQuoteDetail = {
  quotation: {
    quotationNo: string;
    projectTitle: string;
    publicDownloadPdf: boolean;
  };
  html: string;
};

export function PublicQuotePage() {
  const { token } = useParams();
  const { t } = useAppSettings();
  const [meta, setMeta] = useState<PublicQuoteMeta | null>(null);
  const [detail, setDetail] = useState<PublicQuoteDetail | null>(null);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const previewRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    let active = true;

    async function loadMeta() {
      if (!token) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await apiGet<PublicQuoteMeta>(`/api/public/q/${token}/meta`);

        if (!active) {
          return;
        }

        setMeta(response);
        await loadDetailIfVerified(token, active);
      } catch (loadError) {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : t("public.loadError"));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    async function loadDetailIfVerified(publicToken: string, stillActive: boolean) {
      try {
        const response = await apiGet<PublicQuoteDetail>(`/api/public/q/${publicToken}`);

        if (stillActive) {
          setDetail(response);
        }
      } catch {
        setDetail(null);
      }
    }

    void loadMeta();

    return () => {
      active = false;
    };
  }, [token, t]);

  async function handleVerify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!token) {
      return;
    }

    setVerifying(true);
    setError(null);

    try {
      await apiPost(`/api/public/q/${token}/verify`, { password });
      const response = await apiGet<PublicQuoteDetail>(`/api/public/q/${token}`);
      setDetail(response);
    } catch (verifyError) {
      setError(verifyError instanceof Error ? verifyError.message : t("public.verifyError"));
    } finally {
      setVerifying(false);
    }
  }

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="px-4 py-3 text-sm text-muted-foreground">{t("public.loading")}</Card>
      </main>
    );
  }

  if (!detail) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="absolute right-4 top-4">
          <PreferenceControls />
        </div>
        <Card className="w-full max-w-sm p-6">
          <h1 className="text-lg font-semibold">{meta?.projectTitle ?? t("public.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{meta?.quotationNo ?? t("public.description")}</p>
          {error ? <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div> : null}
          <form className="mt-5 space-y-3" onSubmit={handleVerify}>
            <Input type="password" placeholder={t("public.password")} value={password} onChange={(event) => setPassword(event.target.value)} />
            <Button className="w-full" type="submit">
              {verifying ? t("public.verifying") : t("public.view")}
            </Button>
          </form>
        </Card>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background px-4 py-4">
      <section className="mx-auto max-w-6xl space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold">{detail.quotation.projectTitle}</h1>
            <p className="text-xs text-muted-foreground">{detail.quotation.quotationNo}</p>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            {detail.quotation.publicDownloadPdf ? (
              <Button type="button" variant="secondary" onClick={() => previewRef.current?.contentWindow?.print()}>
                {t("public.print")}
              </Button>
            ) : null}
            <PreferenceControls />
          </div>
        </div>
        <Card className="overflow-hidden p-0">
          <iframe
            ref={previewRef}
            title={t("public.frameTitle")}
            srcDoc={detail.html}
            className="h-[calc(100vh-76px)] min-h-[640px] w-full bg-white"
          />
        </Card>
      </section>
    </main>
  );
}
